import React, { memo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MAX_REPORT_IMAGES } from "../constants";
import "./styles/PhotoSelectionDialog.css";

interface PhotoSelectionDialogProps {
  onClose: () => void;
  onPhotosSelected: (files: File[]) => void;
  onAdminClean?: () => void;
  onAdminNotFound?: () => void;
  isAdmin?: boolean;
}

const PhotoSelectionDialog: React.FC<PhotoSelectionDialogProps> = memo(
  ({ onClose, onPhotosSelected, onAdminClean, onAdminNotFound, isAdmin }) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        let files = Array.from(e.target.files || []);
        // Reset so re-picking the same files still fires onChange.
        e.target.value = "";

        if (files.length > MAX_REPORT_IMAGES) {
          alert(t("report.tooManyImages", { max: MAX_REPORT_IMAGES }));
          files = files.slice(0, MAX_REPORT_IMAGES);
        }

        if (files.length > 0) {
          onPhotosSelected(files);
          onClose();
        }
      },
      [onPhotosSelected, onClose, t]
    );

    const handleChooseFromDevice = useCallback(() => {
      fileInputRef.current?.click();
    }, []);

    const handleAdminCleanClick = useCallback(() => {
      if (onAdminClean) {
        onAdminClean();
      }
    }, [onAdminClean]);

    const handleAdminNotFoundClick = useCallback(() => {
      if (onAdminNotFound) {
        onAdminNotFound();
      }
    }, [onAdminNotFound]);

    const handleOverlayClick = useCallback(
      (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      },
      [onClose]
    );

    const handleDialogClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    return (
      <div className="photo-dialog-overlay" onClick={handleOverlayClick}>
        <div className="photo-dialog" onClick={handleDialogClick}>
          <h3 className="photo-dialog-title">{t("report.addPhotos")}</h3>

          <div className="photo-dialog-options">
            <button
              className="photo-option-button"
              onClick={handleChooseFromDevice}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/gallary.png`}
                alt={t("report.chooseFromDevice")}
                className="photo-option-icon"
              />
              <span className="photo-option-text">{t("report.chooseFromDevice")}</span>
            </button>

            {isAdmin && onAdminClean && (
              <button
                className="photo-option-button"
                onClick={handleAdminCleanClick}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/edit_prod.png`}
                  alt={t("cleanup.cleanedByAdmin")}
                  className="photo-option-icon"
                />
                <span className="photo-option-text">
                  {t("cleanup.cleanedByAdmin")}
                </span>
              </button>
            )}

            {isAdmin && onAdminNotFound && (
              <button
                className="photo-option-button"
                onClick={handleAdminNotFoundClick}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/edit_prod.png`}
                  alt={t("cleanup.notFoundByAdmin")}
                  className="photo-option-icon"
                />
                <span className="photo-option-text">
                  {t("cleanup.notFoundByAdmin")}
                </span>
              </button>
            )}
          </div>

          <button className="photo-dialog-cancel" onClick={onClose}>
            {t("common.cancel")}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
        </div>
      </div>
    );
  }
);

PhotoSelectionDialog.displayName = "PhotoSelectionDialog";

export default PhotoSelectionDialog;
